/**
 * InvoicePositionEditor.jsx (L1–L3)
 * Position eines Rechnungsentwurfs bearbeiten:
 * - Bezeichnung und Beschreibung (von Hand ändern oder neu aus Vorlage und Buchungen erzeugen)
 * - Buchungen hinter der Position: abgerechnete Stunden je Buchung anpassen (mit Begründung),
 *   Text nur für diese Rechnung, Notiz im Text ein-/ausblenden.
 * Die Zeitbuchung selbst (gebuchte Stunden, Notiz) bleibt immer unverändert.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, RotateCcw, Pencil } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';

const fmtHours = (h) =>
  h === null || h === undefined ? '' : Number(h).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDay = (iso) => {
  if (!iso) return '';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return d && m ? `${d}.${m}.` : iso;
};

const fmtEur = (v) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(v || 0);

const errorText = (error) => {
  const d = error?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x.msg).join(', ');
  return 'Aktion fehlgeschlagen';
};

export default function InvoicePositionEditor({ open, onOpenChange, invoice, position, api, canEdit, onInvoiceChanged }) {
  const [anchor, setAnchor] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [savingText, setSavingText] = useState(false);
  const [entriesData, setEntriesData] = useState(null);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [editingEntryId, setEditingEntryId] = useState(null);
  const [entryForm, setEntryForm] = useState({ billed_hours: '', reason: '', text: '' });
  const [savingEntry, setSavingEntry] = useState(false);

  // Position über eine ihrer Buchungen wiederfinden – die Nummer kann sich beim Neuberechnen ändern
  const item = useMemo(() => {
    const items = invoice?.line_items || [];
    if (anchor) {
      const found = items.find((i) => (i.source_entry_ids || []).includes(anchor));
      if (found) return found;
    }
    return items.find((i) => i.position === position) || null;
  }, [invoice, anchor, position]);

  useEffect(() => {
    if (!open) return;
    const items = invoice?.line_items || [];
    const start = items.find((i) => i.position === position);
    setAnchor(start?.source_entry_ids?.[0] || null);
    setEditingEntryId(null);
    // nur beim Öffnen neu setzen
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, position]);

  useEffect(() => {
    if (!item) return;
    setTitle(item.title || '');
    setDescription(item.description || '');
  }, [item?.title, item?.description, item?.position]); // eslint-disable-line react-hooks/exhaustive-deps

  const isDraft = invoice?.status === 'draft';
  const editable = isDraft && canEdit;
  const hasEntries = item && ['hours', 'daily', 'surcharge'].includes(item.type) && (item.source_entry_ids || []).length > 0;

  const loadEntries = useCallback(async () => {
    if (!invoice?.id || !item || !hasEntries) {
      setEntriesData(null);
      return;
    }
    setLoadingEntries(true);
    try {
      const res = await api.get(`/billing/invoices/${invoice.id}/line-items/${item.position}/entries`);
      setEntriesData(res.data);
    } catch (error) {
      toast.error(errorText(error));
    } finally {
      setLoadingEntries(false);
    }
  }, [api, invoice?.id, item?.position, hasEntries]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open) loadEntries();
  }, [open, loadEntries, invoice?.line_items]);

  const applyInvoice = (updated) => {
    if (updated) onInvoiceChanged(updated);
  };

  const saveTexts = async () => {
    if (!item) return;
    setSavingText(true);
    try {
      await api.put(`/billing/invoices/${invoice.id}/line-items/${item.position}`, { title, description });
      const res = await api.get(`/billing/invoices/${invoice.id}`);
      applyInvoice(res.data);
      toast.success('Position gespeichert');
    } catch (error) {
      toast.error(errorText(error));
    } finally {
      setSavingText(false);
    }
  };

  const regenerate = async () => {
    if (!item) return;
    setSavingText(true);
    try {
      const res = await api.post(`/billing/invoices/${invoice.id}/line-items/${item.position}/regenerate`);
      applyInvoice(res.data.invoice);
      toast.success('Text neu erzeugt');
    } catch (error) {
      toast.error(errorText(error));
    } finally {
      setSavingText(false);
    }
  };

  const startEdit = (row) => {
    setEditingEntryId(row.id);
    setEntryForm({
      billed_hours: row.billed_hours !== null && row.billed_hours !== undefined ? String(row.billed_hours) : String(row.hours ?? ''),
      reason: row.adjustment_reason || '',
      text: row.invoice_text || '',
    });
  };

  const saveEntry = async (row, reset = false) => {
    setSavingEntry(true);
    try {
      let updated = null;
      const newBilled = reset ? null : parseFloat(String(entryForm.billed_hours).replace(',', '.'));
      const currentBilled = row.billed_hours ?? null;
      const billedChanged = reset ? currentBilled !== null : (!Number.isNaN(newBilled) && newBilled !== (currentBilled ?? row.hours));
      if (row.may_adjust && !row.is_daily_rate && billedChanged) {
        const res = await api.put(`/billing/invoices/${invoice.id}/entries/${row.id}/billing-quantity`, {
          billed_hours: reset ? null : newBilled,
          adjustment_reason: entryForm.reason,
        });
        updated = res.data.invoice;
      }
      const newText = reset ? '' : entryForm.text.trim();
      if ((row.invoice_text || '') !== newText) {
        const res = await api.put(`/billing/invoices/${invoice.id}/entries/${row.id}/text`, { text: newText || null });
        updated = res.data.invoice;
      }
      if (updated) {
        applyInvoice(updated);
        toast.success(reset ? 'Zurückgesetzt' : 'Übernommen');
      }
      setEditingEntryId(null);
    } catch (error) {
      toast.error(errorText(error));
    } finally {
      setSavingEntry(false);
    }
  };

  const toggleNote = async (row, visible) => {
    try {
      const res = await api.put(`/billing/invoices/${invoice.id}/entries/${row.id}/text`, { hide_note: !visible });
      applyInvoice(res.data.invoice);
    } catch (error) {
      toast.error(errorText(error));
    }
  };

  if (!item) return null;

  const fromTemplate = !!item.category_id;
  const rows = entriesData?.entries || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border text-foreground max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-foreground">Position {item.position} bearbeiten</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {item.quantity} {item.unit} × {fmtEur(item.unit_price)} = {fmtEur(item.net_amount)}
            {item.period_start && item.period_end ? ` · Leistungszeitraum ${fmtDay(item.period_start)}–${fmtDay(item.period_end)}${String(item.period_end).slice(0, 4)}` : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex flex-wrap gap-2 text-xs">
            {fromTemplate && !item.title_edited && !item.description_edited && (
              <span className="px-2 py-0.5 rounded bg-sky-500/15 text-sky-700 dark:text-sky-300">aus Vorlage erzeugt</span>
            )}
            {(item.title_edited || item.description_edited) && (
              <span className="px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-700 dark:text-cyan-300">von Hand bearbeitet</span>
            )}
            {item.adjusted_entries > 0 && (
              <span className="px-2 py-0.5 rounded bg-amber-500/15 text-amber-700 dark:text-amber-300">
                {item.adjusted_entries} Buchung{item.adjusted_entries === 1 ? '' : 'en'} angepasst
              </span>
            )}
          </div>

          {item.description_outdated && (
            <div className="flex items-start gap-2 p-3 rounded-md bg-amber-500/10 border border-amber-500/30 text-sm text-amber-800 dark:text-amber-300">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>
                Seit der Handänderung haben sich Buchungen oder Texte geändert. Der Text oben ist der von Hand geschriebene.
                „Neu aus Buchungen erzeugen“ übernimmt den aktuellen Stand.
              </span>
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-muted-foreground">Bezeichnung (BT-153)</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={!editable}
              className="bg-input border-border text-foreground"
            />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <Label className="text-muted-foreground">Beschreibung (BT-154)</Label>
              {editable && item.generated_description !== undefined && (
                <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={regenerate} disabled={savingText}>
                  <RotateCcw className="w-3.5 h-3.5 mr-1" />
                  Neu aus Buchungen erzeugen
                </Button>
              )}
            </div>
            <Textarea
              rows={6}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={!editable}
              className="bg-input border-border text-foreground"
            />
          </div>

          {hasEntries && (
            <div className="space-y-2">
              <div className="flex items-end justify-between gap-2">
                <div>
                  <p className="text-sm font-medium text-foreground">Zugrunde liegende Buchungen</p>
                  <p className="text-xs text-muted-foreground">
                    {rows.length} Buchung{rows.length === 1 ? '' : 'en'}
                    {entriesData ? ` · ${fmtHours(entriesData.hours_recorded)} h gebucht · ${fmtHours(entriesData.hours_billed)} h abgerechnet` : ''}
                    {editable ? ' · Haken = Notiz erscheint im Text' : ''}
                  </p>
                </div>
              </div>
              <div className="border border-border rounded-lg overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-xs text-muted-foreground">
                    <tr>
                      <th className="text-left font-medium px-3 py-2">Datum</th>
                      <th className="text-left font-medium px-2 py-2">MA</th>
                      <th className="text-left font-medium px-2 py-2">Tätigkeit</th>
                      <th className="text-left font-medium px-2 py-2">Notiz / Text</th>
                      <th className="text-right font-medium px-2 py-2">gebucht</th>
                      <th className="text-right font-medium px-2 py-2">abgerechnet</th>
                      <th className="text-center font-medium px-2 py-2">im Text</th>
                      <th className="px-2 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingEntries && rows.length === 0 && (
                      <tr><td colSpan={8} className="px-3 py-3 text-muted-foreground">Lade …</td></tr>
                    )}
                    {rows.map((row) => {
                      const adjusted = row.billed_hours !== null && row.billed_hours !== undefined;
                      const delta = adjusted ? Math.round((row.billed_hours - row.hours) * 100) / 100 : 0;
                      const isEditing = editingEntryId === row.id;
                      return [
                        <tr key={row.id} className={`border-t border-border ${adjusted ? 'bg-amber-500/5' : ''}`}>
                          <td className="px-3 py-2 whitespace-nowrap">{fmtDay(row.date)}</td>
                          <td className="px-2 py-2" title={row.user_name || ''}>{row.user_initials || '–'}</td>
                          <td className="px-2 py-2">{row.activity || <span className="text-muted-foreground">–</span>}</td>
                          <td className="px-2 py-2">
                            {row.invoice_text ? (
                              <span>{row.invoice_text} <span className="text-[11px] text-sky-700 dark:text-sky-300">(für diese Rechnung)</span></span>
                            ) : (
                              <span className="text-muted-foreground">{row.description || '–'}</span>
                            )}
                          </td>
                          <td className="px-2 py-2 text-right text-muted-foreground whitespace-nowrap">{row.is_daily_rate ? 'Tag' : fmtHours(row.hours)}</td>
                          <td className={`px-2 py-2 text-right whitespace-nowrap ${adjusted ? 'font-semibold text-amber-700 dark:text-amber-300' : ''}`}>
                            {row.is_daily_rate ? 'Tag' : fmtHours(row.effective_hours)}
                            {adjusted && delta !== 0 && <span className="ml-1 text-[11px]">{delta > 0 ? '+' : ''}{fmtHours(delta)}</span>}
                          </td>
                          <td className="px-2 py-2 text-center">
                            <Checkbox
                              checked={!row.hide_note}
                              disabled={!editable || !fromTemplate}
                              onCheckedChange={(checked) => toggleNote(row, !!checked)}
                              aria-label="Notiz im Text"
                            />
                          </td>
                          <td className="px-2 py-2 text-right">
                            {editable && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="w-7 h-7"
                                onClick={() => (isEditing ? setEditingEntryId(null) : startEdit(row))}
                                aria-label="Buchung für diese Rechnung anpassen"
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </td>
                        </tr>,
                        isEditing && (
                          <tr key={`${row.id}-edit`} className="bg-amber-500/5">
                            <td colSpan={8} className="px-3 pb-3 pt-1">
                              <div className="grid grid-cols-1 sm:grid-cols-[130px_1fr] gap-3">
                                <div className="space-y-1">
                                  <Label className="text-xs text-muted-foreground">Abgerechnet (h)</Label>
                                  <Input
                                    type="number"
                                    min="0"
                                    step="0.25"
                                    value={entryForm.billed_hours}
                                    onChange={(e) => setEntryForm({ ...entryForm, billed_hours: e.target.value })}
                                    disabled={!row.may_adjust || row.is_daily_rate}
                                    className="bg-input border-border text-foreground h-9"
                                  />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs text-muted-foreground">Begründung (Pflicht bei Abweichung)</Label>
                                  <Input
                                    value={entryForm.reason}
                                    onChange={(e) => setEntryForm({ ...entryForm, reason: e.target.value })}
                                    disabled={!row.may_adjust || row.is_daily_rate}
                                    placeholder="z. B. mit Kunde vereinbarte Mindestabrechnung"
                                    className="bg-input border-border text-foreground h-9"
                                  />
                                </div>
                                <div className="space-y-1 sm:col-span-2">
                                  <Label className="text-xs text-muted-foreground">Text für diese Rechnung (leer = Notiz der Buchung)</Label>
                                  <Input
                                    value={entryForm.text}
                                    onChange={(e) => setEntryForm({ ...entryForm, text: e.target.value })}
                                    placeholder={row.description || ''}
                                    maxLength={300}
                                    className="bg-input border-border text-foreground h-9"
                                  />
                                </div>
                              </div>
                              {!row.may_adjust && !row.is_daily_rate && (
                                <p className="text-xs text-muted-foreground mt-2">Die Stunden dieser Buchung darfst du nicht anpassen – nur den Text.</p>
                              )}
                              <div className="flex flex-wrap items-center justify-between gap-2 mt-3">
                                <p className="text-xs text-amber-800 dark:text-amber-300 max-w-xl">
                                  Die Zeitbuchung selbst bleibt unverändert (Arbeitszeitnachweis). Die Anpassung steht im Änderungsprotokoll und unter „Abrechnungskorrekturen“.
                                </p>
                                <div className="flex gap-2">
                                  <Button type="button" variant="outline" size="sm" disabled={savingEntry} onClick={() => saveEntry(row, true)}>
                                    Zurücksetzen
                                  </Button>
                                  <Button type="button" size="sm" disabled={savingEntry} onClick={() => saveEntry(row)} className="bg-indigo-500 hover:bg-indigo-600">
                                    Übernehmen
                                  </Button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        ),
                      ];
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-muted-foreground">
                Anpassen geht nur im Entwurf. Nach der Freigabe ist die Menge fest. Rechnung und Einzelnachweis zeigen die abgerechneten Stunden.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Schließen
          </Button>
          {editable && (
            <Button
              type="button"
              onClick={saveTexts}
              disabled={savingText || (title === (item.title || '') && description === (item.description || ''))}
              className="bg-indigo-500 hover:bg-indigo-600"
            >
              {savingText ? 'Speichern …' : 'Text speichern'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
